import { readonly, ref, shallowRef, watch } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { useEventsOmnisearchForm } from "./useEventsOmnisearchForm";

/**
 * Owns query commits, suggestion selection, applied state and silent restoration.
 * Draft edits do not change the applied criterion reported by the finder contribution.
 *
 * @param {object} options
 * @param {object} options.props - reactive omnisearch props
 * @param {import('vue').Ref<boolean>} options.disabled
 * @param {ReturnType<import('#ce/shared/filters/composables/useQuantumSearchField').useQuantumSearchField>} options.field
 */
export function useEventsOmnisearchQuery({ props, disabled, field }) {
  const host = useHostElement();
  const { commitFreeText, commitFilters, clearCommits, mirrorQuery, emitChange, navigate, onFormReset } =
    useEventsOmnisearchForm({ disabled });
  const selectedValues = ref([]);
  const appliedQuery = shallowRef("");
  // A seed or suggestion echo is already committed, so blur cannot reinterpret it as free text.
  let lastCommittedText = "";

  function clearSelection() {
    selectedValues.value = [];
  }

  function clearAppliedQuery() {
    appliedQuery.value = "";
    field.setText("");
    lastCommittedText = "";
    clearSelection();
    clearCommits();
    emitChange();
  }

  function restoreAppliedQuery() {
    appliedQuery.value = (props.search ?? "").trim();
    if (props.contributesTo) {
      clearSelection();
      clearCommits();
      mirrorQuery(appliedQuery.value);
    }
  }

  function handleContributionTargetChange(reference, previous) {
    if (reference && !previous) {
      mirrorQuery(appliedQuery.value);
    }
  }

  // Outside contribution mode prefill is text only (legacy parity). A contributor
  // seeds its form value silently too. Prop-driven text never commits as a user edit.
  function restoreSearch(value) {
    field.setText(value ?? "");
    lastCommittedText = value ?? "";
    restoreAppliedQuery();
  }

  function commitFreeTextIfChanged() {
    const text = field.inputText.value;
    if (text === lastCommittedText) {
      return;
    }
    lastCommittedText = text;
    commitFreeText(text);
    emitBubbling("change", text);
  }

  // Schedule rows navigate. Other rows stage filters and echo their title;
  // fallback rows stay unselected so re-picking one continues to register.
  function commitItem(item) {
    const payload = item.payload ?? {};
    emitBubbling("select", selectDetail(item));
    if (payload.url) {
      navigate(payload.url);
      return;
    }
    selectedValues.value = !item.fallback && item.value != null ? [item.value] : [];
    field.setText(item.title ?? "");
    lastCommittedText = item.title ?? "";
    commitFilters(payload.filters);
    emitBubbling("change", selectDetail(item));
  }

  function reset() {
    clearCommits();
    clearSelection();
    restoreSearch(props.search);
  }

  // The legacy app's event payload is direct, rather than the CE argument array.
  function emitBubbling(eventType, payload) {
    host?.dispatchEvent(new CustomEvent(eventType, { detail: payload, bubbles: true }));
  }

  watch(() => props.search, restoreSearch, { immediate: true });
  watch(() => props.contributesTo, handleContributionTargetChange);
  onFormReset(reset);

  return {
    selectedValues: readonly(selectedValues),
    appliedQuery: readonly(appliedQuery),
    clearSelection,
    clearAppliedQuery,
    commitFreeTextIfChanged,
    commitItem,
  };
}

function selectDetail(item) {
  return { text: item.title, group: item.group, ...(item.payload ?? {}) };
}
